﻿import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Users,
  Truck,
  ShoppingCart,
  Receipt,
  Package,
  Boxes,
  UserCog,
  Settings,
  ShieldCheck,
  User,
  LogOut,
  Menu,
  X,
  ChevronsLeft,
  ChevronsRight,
  CalendarDays,
  Target,
  Bell,
  Sun,
  Moon,
  Activity,
  AlertTriangle,
  BarChart3,
  Building2,
  ClipboardList,
  CreditCard,
  DollarSign,
  Gauge,
  Info,
  Layers3,
  LifeBuoy,
  LineChart,
  TrendingUp,
  Users2,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "../contexts/ThemeContext";
import { useToast } from "../hooks/useToast";
import { usePermissions } from "../hooks/usePermissions";
import { useMyBusiness } from "../hooks/useBusiness";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useBusinessLanguage } from "../hooks/useBusinessLanguage";
import { useUnreadCount } from "../hooks/useNotifications";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { NotificationPanel } from "./NotificationPanel";
import { HelpMenu } from "./HelpMenu";
import { OnboardingChecklistModal } from "./onboarding/OnboardingChecklistModal";
import { LegalUpdatePrompt } from "./legal/LegalUpdatePrompt";
import { BrandMark } from "./BrandMark";
import type { SectionKey } from "../api/types";

// ─── Tone palette ────────────────────────────────────────────────────────────
//
// Each nav item carries a `tone` matching the section it represents (e.g.
// Sales = emerald, Suppliers = orange). The active row uses a NEUTRAL
// slate wash (see the NavRow component below) — the section's left-edge
// bar (`TONE_BAR[tone]`) is the only color identity on the row. The
// icon also keeps its section tone.
//
// The `"violet"` tone is the one exception that's wired to the user's
// primary accent: the Goals nav item gets the same tint as the
// dashboard's Net hero, so a user who picks gold as their accent sees
// the Goals icon retint with it. All other tones are literal Tailwind
// colors (semantic per section, never picker-driven). See ADR 0002 for
// the full Tier A (fixed) vs Tier B (accent) split.
//
// Hover state uses a faint section-tone wash so the row pre-announces what
// it'll look like when active.

type NavTone =
  | "blue"
  | "emerald"
  | "amber"
  | "orange"
  | "purple"
  | "cyan"
  | "indigo"
  | "teal"
  | "violet"
  | "rose"
  | "slate";

const TONE_ICON: Record<NavTone, string> = {
  blue: "text-blue-600 dark:text-blue-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
  amber: "text-amber-600 dark:text-amber-400",
  orange: "text-orange-600 dark:text-orange-400",
  purple: "text-purple-600 dark:text-purple-400",
  cyan: "text-cyan-600 dark:text-cyan-400",
  indigo: "text-indigo-600 dark:text-indigo-400",
  teal: "text-teal-600 dark:text-teal-400",
  violet: "text-accent-600 dark:text-accent-400",
  rose: "text-rose-600 dark:text-rose-400",
  slate: "text-fg-secondary",
};

const TONE_BAR: Record<NavTone, string> = {
  blue: "bg-blue-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  orange: "bg-orange-500",
  purple: "bg-purple-500",
  cyan: "bg-cyan-500",
  indigo: "bg-indigo-500",
  teal: "bg-teal-500",
  violet: "bg-accent-500",
  rose: "bg-rose-500",
  slate: "bg-slate-400",
};

const TONE_HOVER: Record<NavTone, string> = {
  blue: "hover:bg-blue-50 dark:hover:bg-blue-900/20",
  emerald: "hover:bg-emerald-50 dark:hover:bg-emerald-900/20",
  amber: "hover:bg-amber-50 dark:hover:bg-amber-900/20",
  orange: "hover:bg-orange-50 dark:hover:bg-orange-900/20",
  purple: "hover:bg-purple-50 dark:hover:bg-purple-900/20",
  cyan: "hover:bg-cyan-50 dark:hover:bg-cyan-900/20",
  indigo: "hover:bg-indigo-50 dark:hover:bg-indigo-900/20",
  teal: "hover:bg-teal-50 dark:hover:bg-teal-900/20",
  violet: "hover:bg-accent-50 dark:hover:bg-accent-900/20",
  rose: "hover:bg-rose-50 dark:hover:bg-rose-900/20",
  slate: "hover:bg-slate-100 dark:hover:bg-slate-800",
};

// ─── Per-route document title ────────────────────────────────────────────────
//
// Each authenticated section drives `document.title = "Adam | <Section>"`.
// Keep this list aligned with `navItems` / `secondaryItems` below — adding a
// new section needs both a nav entry and a title entry.
const PATH_TITLE_KEYS: Array<{ to: string; key: string; end?: boolean }> = [
  { to: "/", key: "nav.dashboard", end: true },
  { to: "/customers", key: "nav.customers" },
  { to: "/suppliers", key: "nav.suppliers" },
  { to: "/sales", key: "nav.sales" },
  { to: "/expenses", key: "nav.expenses" },
  { to: "/catalog", key: "nav.catalog" },
  { to: "/inventory", key: "nav.inventory" },
  { to: "/calendar", key: "nav.calendar" },
  { to: "/goals", key: "nav.goals" },
  { to: "/notifications", key: "nav.notifications" },
  { to: "/employees", key: "nav.employees" },
  { to: "/settings", key: "nav.businessSettings" },
  { to: "/account", key: "nav.accountSettings" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STORAGE_KEY = "adam-sidebar-collapsed";
const WIDTH_KEY = "adam-sidebar-width";
const MIN_WIDTH = 180;
const MAX_WIDTH = 420;
const DEFAULT_WIDTH = 240; // matches the previous md:w-60
const COLLAPSED_WIDTH = 60;

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function readWidth(): number {
  try {
    const raw = localStorage.getItem(WIDTH_KEY);
    const n = raw ? Number(raw) : NaN;
    if (!Number.isFinite(n)) return DEFAULT_WIDTH;
    return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, n));
  } catch {
    return DEFAULT_WIDTH;
  }
}

// ─── Tooltip wrapper (shows label on hover when sidebar is collapsed) ────────

function NavTooltip({
  label,
  collapsed,
  children,
}: {
  label: string;
  collapsed: boolean;
  children: React.ReactNode;
}) {
  if (!collapsed) return <>{children}</>;
  return (
    <div className="relative group/tip">
      {children}
      <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 group-hover/tip:opacity-100 transition-opacity shadow-lg dark:bg-slate-700">
        {label}
      </span>
    </div>
  );
}

// ─── Sidebar nav row ─────────────────────────────────────────────────────────

interface NavRowProps {
  to: string;
  end?: boolean;
  icon: React.ElementType;
  label: string;
  tone: NavTone;
  collapsed: boolean;
  onClick?: () => void;
}

/**
 * Single NavLink row with the new active treatment:
 *   - left-edge accent bar in the section's tone
 *   - subtle violet background wash
 *   - violet text + section-tone icon
 *
 * Inactive: slate text + bare icon, hover gets a faint section-tone wash.
 */
function NavRow({ to, end, icon: Icon, label, tone, collapsed, onClick }: NavRowProps) {
  return (
    <NavTooltip label={label} collapsed={collapsed}>
      <NavLink
        to={to}
        end={end}
        onClick={onClick}
        title={collapsed ? label : undefined}
        className={({ isActive }) =>
          `group relative flex items-center rounded-lg text-sm font-medium transition-colors duration-150 ${
            collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
          } ${
            isActive
              ? // Active row uses a NEUTRAL slate wash so the section's
                // left-edge bar (`TONE_BAR[tone]`) is the only color
                // identity — Sales = emerald bar, Suppliers = orange bar,
                // etc. The wash + text stay neutral regardless of the
                // user's primary accent (see ADR 0002 — Tier A vs Tier B).
                "bg-slate-100 text-slate-900 dark:bg-white/10 dark:text-white"
              : `text-fg-secondary ${TONE_HOVER[tone]} hover:text-slate-900 dark:hover:text-white`
          }`
        }
      >
        {({ isActive }) => (
          <>
            {/* Left-edge accent bar — visible only when active */}
            <span
              aria-hidden="true"
              className={`absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full transition-opacity ${
                TONE_BAR[tone]
              } ${isActive ? "opacity-100" : "opacity-0"}`}
            />
            <Icon
              className={`h-4 w-4 flex-shrink-0 transition-colors ${
                isActive
                  ? TONE_ICON[tone]
                  : // Inactive icons stay neutral slate in BOTH light + dark
                    // modes (no hover tinting on the icon itself — the row bg
                    // gets the section-tone wash on hover). Also avoids a
                    // dynamic Tailwind class issue: `group-hover:${TONE_ICON}`
                    // produced composite classes the JIT scanner couldn't see.
                    "text-fg-muted"
              }`}
            />
            {!collapsed && <span>{label}</span>}
          </>
        )}
      </NavLink>
    </NavTooltip>
  );
}

// Shown inside the content area while a lazy route chunk downloads (ADR 0013)
// — the sidebar and header stay on screen, only the page body swaps.
function OutletFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center py-24" role="status" aria-label={t("common.loading")}>
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900 dark:border-slate-700 dark:border-t-slate-100" />
    </div>
  );
}

// ─── AppLayout ───────────────────────────────────────────────────────────────

export function AppLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { addToast } = useToast();
  const { isPlatformAdmin, isBusinessAdmin, canView } = usePermissions();
  const navigate = useNavigate();
  const { data: business, isError: noBusiness } = useMyBusiness();
  // Per-business feature flags — when off, hide the nav row entirely.
  // Goals shares the "notifications" SectionKey so it has no dedicated flag;
  // it self-gates by goal count.
  const inventoryEnabled = useFeatureFlag("inventory_enabled");
  const suppliersEnabled = useFeatureFlag("suppliers_enabled");
  const catalogEnabled = useFeatureFlag("catalog_enabled");
  const calendarEnabled = useFeatureFlag("calendar_enabled");
  const employeesEnabled = useFeatureFlag("employees_enabled");
  const isFeatureEnabledForRoute = (to: string): boolean => {
    if (to === "/inventory") return inventoryEnabled;
    if (to === "/suppliers") return suppliersEnabled;
    if (to === "/catalog") return catalogEnabled;
    if (to === "/calendar") return calendarEnabled;
    if (to === "/employees") return employeesEnabled;
    return true;
  };
  // Sync UI language to the business preference. Respects an explicit user
  // override stored in sessionStorage (set when the user picks a language
  // manually) so this hook doesn't fight the user's choice.
  useBusinessLanguage(business?.language);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [width, setWidth] = useState<number>(readWidth);
  const [resizing, setResizing] = useState(false);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [checklistModalOpen, setChecklistModalOpen] = useState(false);
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count ?? 0;
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const widthRef = useRef(width);
  widthRef.current = width;

  // Mobile drawer swipe-to-close: track a leftward drag on the open drawer and
  // dismiss it past a threshold. Guarded on `sidebarOpen` so it never fires on
  // the desktop (relative) sidebar.
  const [drawerDragX, setDrawerDragX] = useState(0);
  const drawerTouch = useRef<{ x: number; y: number; dragging: boolean } | null>(null);

  // Drive the tab title from the active route: "Adam | <Section>".
  // Falls back to "Adam" on routes outside the table (landing/login/onboarding).
  useEffect(() => {
    const path = location.pathname;
    const match = PATH_TITLE_KEYS
      .filter(({ to, end }) => (end ? path === to : path === to || path.startsWith(`${to}/`)))
      .sort((a, b) => b.to.length - a.to.length)[0];
    document.title = match ? `Adam | ${t(match.key)}` : "Adam";
    return () => {
      document.title = "Adam";
    };
  }, [location.pathname, t]);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setResizing(true);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (ev: MouseEvent) => {
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, ev.clientX));
      setWidth(next);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
      setResizing(false);
      try {
        localStorage.setItem(WIDTH_KEY, String(widthRef.current));
      } catch {
        // ignore quota / privacy mode
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  // Cleanup the body styles if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      if (resizing) {
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };
  }, [resizing]);

  const handleLogout = async () => {
    await logout();
    addToast(t("nav.loggedOutToast"), "info");
    navigate("/login");
  };

  const closeSidebar = () => setSidebarOpen(false);

  const onDrawerTouchStart = (e: React.TouchEvent) => {
    if (!sidebarOpen) return;
    const t0 = e.touches[0];
    drawerTouch.current = { x: t0.clientX, y: t0.clientY, dragging: false };
  };
  const onDrawerTouchMove = (e: React.TouchEvent) => {
    const start = drawerTouch.current;
    if (!start) return;
    const dx = e.touches[0].clientX - start.x;
    const dy = e.touches[0].clientY - start.y;
    // Only hijack the gesture once it's clearly a leftward horizontal swipe —
    // otherwise let the nav list scroll vertically.
    if (!start.dragging) {
      if (dx > -8 || Math.abs(dx) <= Math.abs(dy)) return;
      start.dragging = true;
    }
    setDrawerDragX(Math.min(0, Math.max(dx, -320)));
  };
  const onDrawerTouchEnd = () => {
    const start = drawerTouch.current;
    drawerTouch.current = null;
    const shouldClose = !!start?.dragging && drawerDragX < -64;
    setDrawerDragX(0);
    if (shouldClose) closeSidebar();
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* localStorage may be unavailable */ }
      return next;
    });
  };

  const hasBusiness = !!business && !noBusiness;

  // ── No business state ──────────────────────────────────────────────────────
  // Platform admins don't need a Business — they operate above the tenant layer,
  // so we keep the full sidebar (with the Platform Admin entry) instead of the
  // stripped onboarding chrome.
  if (!hasBusiness && !isPlatformAdmin) {
    return (
      <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
        <header className="flex h-14 items-center justify-between border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark size={28} />
            <span className="text-sm font-semibold text-fg-primary">Adam</span>
          </Link>
          {user && (
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-sm text-fg-secondary">{user.full_name}</span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-fg-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{t("nav.logout")}</span>
              </button>
            </div>
          )}
        </header>
        <main className="mx-auto w-full max-w-3xl flex-1 px-4 sm:px-6 py-8 sm:py-10">
          <Suspense fallback={<OutletFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    );
  }

  // ── Nav item definitions ───────────────────────────────────────────────────
  const navItems: {
    section: SectionKey;
    to: string;
    end?: boolean;
    icon: React.ElementType;
    tone: NavTone;
    label: string;
  }[] = [
    // Dashboard first (entry point). The five Analytics Hub modules follow
    // in `HUB_SECTIONS` order (Sales · Expenses · Inventory · Customers ·
    // Suppliers) so the sidebar mirrors the hub landing exactly. Non-hub
    // modules (Catalog, Calendar, Goals, Notifications) sit afterward as
    // auxiliary entries. See ADR 0008.
    { section: "dashboard", to: "/", end: true, icon: LayoutDashboard, tone: "slate", label: t("nav.dashboard") },
    { section: "sales", to: "/sales", icon: ShoppingCart, tone: "teal", label: t("nav.sales") },
    { section: "expenses", to: "/expenses", icon: Receipt, tone: "purple", label: t("nav.expenses") },
    { section: "inventory", to: "/inventory", icon: Boxes, tone: "cyan", label: t("nav.inventory") },
    { section: "customers", to: "/customers", icon: Users, tone: "blue", label: t("nav.customers") },
    { section: "suppliers", to: "/suppliers", icon: Truck, tone: "amber", label: t("nav.suppliers") },
    { section: "catalog", to: "/catalog", icon: Package, tone: "orange", label: t("nav.catalog") },
    { section: "calendar", to: "/calendar", icon: CalendarDays, tone: "indigo", label: t("nav.calendar") },
    { section: "notifications", to: "/goals", icon: Target, tone: "violet", label: t("nav.goals") },
    { section: "notifications", to: "/notifications", icon: Bell, tone: "rose", label: t("nav.notifications") },
  ];

  const secondaryItems: typeof navItems = [
    { section: "employees", to: "/employees", icon: UserCog, tone: "slate", label: t("nav.employees") },
    { section: "settings", to: "/settings", icon: Settings, tone: "slate", label: t("nav.businessSettings") },
  ];

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-50 dark:bg-slate-950">
      {/* ── Top bar (full width, spans above the sidebar) ─────────────────── */}
      <header className="flex h-14 flex-shrink-0 items-center justify-between border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm px-4 sm:px-6 lg:px-8">
        {/* Left: brand (visible at all breakpoints) */}
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <BrandMark size={28} className="flex-shrink-0" />
          <span className="text-sm font-semibold text-fg-primary truncate max-w-[160px] sm:max-w-[240px]">
            {business?.name ?? "Adam"}
          </span>
        </Link>

        {/* Right cluster */}
        <div className="flex items-center gap-2">
          {/* User name — no decorative status dot. Hidden below sm to keep
              the mobile cluster tight. */}
          {user && (
            <span className="hidden sm:inline text-sm font-medium text-fg-primary truncate max-w-[200px] pr-1">
              {user.first_name} {user.last_name}
            </span>
          )}

          {/* Theme toggle (light ↔ dark) */}
          <button
            type="button"
            onClick={toggleTheme}
            className="tap-target inline-flex items-center justify-center rounded-lg p-2 text-fg-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label={theme === "dark" ? t("account.switchToLight") : t("account.switchToDark")}
            title={theme === "dark" ? t("account.switchToLight") : t("account.switchToDark")}
          >
            {theme === "dark" ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </button>

          {/* Notification bell. Badge uses the user's primary accent via the
              `accent-*` token (see `src/styles/appearance.css`). */}
          <div className="relative">
            <button
              onClick={() => setNotifPanelOpen((o) => !o)}
              className="tap-target relative inline-flex items-center justify-center rounded-lg p-2 text-fg-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label={t("nav.notifications")}
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent-500 text-[10px] font-bold text-white leading-none shadow-sm">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
            {notifPanelOpen && (
              <NotificationPanel onClose={() => setNotifPanelOpen(false)} />
            )}
          </div>

          {/* Help — reopen the onboarding checklist + in-app documentation. */}
          <div className="relative">
            <button
              onClick={() => setHelpMenuOpen((o) => !o)}
              className="tap-target inline-flex items-center justify-center rounded-lg p-2 text-fg-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label={t("help.title")}
            >
              <Info className="h-5 w-5" />
            </button>
            {helpMenuOpen && (
              <HelpMenu
                onClose={() => setHelpMenuOpen(false)}
                onOpenChecklist={() => {
                  setHelpMenuOpen(false);
                  setChecklistModalOpen(true);
                }}
              />
            )}
          </div>

          {/* Logout — moved out of the sidebar footer into the top bar. */}
          <button
            type="button"
            onClick={handleLogout}
            className="tap-target inline-flex items-center justify-center rounded-lg p-2 text-fg-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label={t("nav.logout")}
            title={t("nav.logout")}
          >
            <LogOut className="h-5 w-5" />
          </button>

          {/* Mobile: hamburger opens the sidebar drawer */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="tap-target inline-flex items-center justify-center rounded-lg p-2 text-fg-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors md:hidden"
            aria-label={t("nav.openMenu")}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* ── Row: sidebar + main content (sits under the top bar) ──────────── */}
      <div className="flex flex-1 min-h-0 relative">
      {/* Mobile backdrop — absolute (not fixed) so it covers the row but
          leaves the top bar interactive on mobile. */}
      {sidebarOpen && (
        <div
          className="absolute inset-0 z-30 bg-black/50 md:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar ──────────────────────────────────────────────────────────── */}
      <aside
        onTouchStart={onDrawerTouchStart}
        onTouchMove={onDrawerTouchMove}
        onTouchEnd={onDrawerTouchEnd}
        className={`
          app-sidebar fixed inset-y-0 left-0 z-40 flex flex-col
          bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
          ${resizing || drawerDragX < 0 ? "" : "transition-[width,transform] duration-300 ease-in-out"}
          ${sidebarOpen ? "translate-x-0 w-72" : "-translate-x-full w-72"}
          md:relative md:translate-x-0 md:w-auto
          flex-shrink-0
        `}
        style={
          isDesktop
            ? { width: collapsed ? COLLAPSED_WIDTH : width }
            : drawerDragX < 0
              ? { transform: `translateX(${drawerDragX}px)` }
              : undefined
        }
      >
        {/* Mobile-only drawer header — just a close (X) button. Brand was
            removed because it duplicates the full-width top bar's brand. On
            desktop the sidebar has no header; nav starts at the top. */}
        <div className="flex md:hidden h-14 items-center justify-end border-b border-slate-200 dark:border-slate-800 px-4">
          <button
            onClick={closeSidebar}
            className="tap-target inline-flex flex-shrink-0 items-center justify-center rounded p-1 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
            aria-label={t("nav.closeMenu")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-4 space-y-0.5">
          {/* Primary nav */}
          {navItems.map((item) =>
            // Dashboard is admin-only — hide it for non-admins regardless of
            // section permissions. Other items use the standard canView gate
            // AND the per-business feature flag for that route.
            (item.section === "dashboard" ? isBusinessAdmin : canView(item.section) && isFeatureEnabledForRoute(item.to)) ? (
              <NavRow
                key={item.to}
                to={item.to}
                end={item.end}
                icon={item.icon}
                label={item.label}
                tone={item.tone}
                collapsed={collapsed}
                onClick={closeSidebar}
              />
            ) : null
          )}

          <div className="my-2 border-t border-slate-200 dark:border-slate-800" />

          {/* Secondary nav */}
          {secondaryItems.map((item) =>
            canView(item.section) && isFeatureEnabledForRoute(item.to) ? (
              <NavRow
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                tone={item.tone}
                collapsed={collapsed}
                onClick={closeSidebar}
              />
            ) : null
          )}

          {/* Account settings (always visible) */}
          <NavRow
            to="/account"
            icon={User}
            label={t("nav.accountSettings")}
            tone="slate"
            collapsed={collapsed}
            onClick={closeSidebar}
          />

          {isPlatformAdmin && (
            <>
              <div className="my-2 border-t border-slate-200 dark:border-slate-800" />
              {!collapsed && (
                <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                  {t("nav.platformSectionLabel")}
                </p>
              )}
              {([
                { to: "/platform/overview", end: true, label: "Overview", icon: ShieldCheck, tone: "rose" as NavTone },
                { to: "/platform/businesses", label: "Businesses", icon: Building2, tone: "rose" as NavTone },
                { to: "/platform/users", label: "Users", icon: Users2, tone: "rose" as NavTone },
                { to: "/platform/subscriptions", label: "Subscriptions", icon: CreditCard, tone: "rose" as NavTone },
                { to: "/platform/revenue", label: "Revenue", icon: DollarSign, tone: "rose" as NavTone },
                { to: "/platform/funnel", label: "Funnel", icon: BarChart3, tone: "rose" as NavTone },
                { to: "/platform/landing", label: "Landing", icon: LineChart, tone: "rose" as NavTone },
                { to: "/platform/retention", label: "Retention", icon: Activity, tone: "rose" as NavTone },
                { to: "/platform/engagement", label: "Engagement", icon: TrendingUp, tone: "rose" as NavTone },
                { to: "/platform/modules", label: "Modules", icon: Layers3, tone: "rose" as NavTone },
                { to: "/platform/alerts", label: "Alerts", icon: AlertTriangle, tone: "rose" as NavTone },
                { to: "/platform/health", label: "Ops", icon: Gauge, tone: "rose" as NavTone },
                { to: "/platform/support", label: "Support", icon: LifeBuoy, tone: "rose" as NavTone },
                { to: "/platform/audit", label: "Audit", icon: ClipboardList, tone: "rose" as NavTone },
              ]).map((item) => (
                <NavRow
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  icon={item.icon}
                  label={item.label}
                  tone={item.tone}
                  collapsed={collapsed}
                  onClick={closeSidebar}
                />
              ))}
            </>
          )}

          {/* Desktop/tablet collapse toggle */}
          <div className="hidden md:block pt-2">
            <button
              onClick={toggleCollapsed}
              title={collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}
              className={`w-full flex items-center rounded-lg px-2 py-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-500 dark:hover:text-white dark:hover:bg-slate-800 transition-colors text-xs font-medium ${
                collapsed ? "justify-center" : "gap-2"
              }`}
            >
              {collapsed ? (
                <ChevronsRight className="h-4 w-4" />
              ) : (
                <>
                  <ChevronsLeft className="h-4 w-4" />
                  <span>{t("nav.collapse")}</span>
                </>
              )}
            </button>
          </div>
        </nav>

        {/* Desktop resize handle — drag to adjust sidebar width. Uses the
            user's primary accent via the `accent-*` token. */}
        {!collapsed && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={t("nav.resizeSidebar")}
            onMouseDown={startResize}
            className={`hidden md:block absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-50 transition-colors ${
              resizing ? "bg-accent-500/60" : "hover:bg-accent-500/40"
            }`}
          />
        )}
      </aside>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950">
          <main className="mx-auto w-full max-w-[1920px] px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <Suspense fallback={<OutletFallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
      </div>
      <LegalUpdatePrompt />
      <OnboardingChecklistModal
        open={checklistModalOpen}
        onClose={() => setChecklistModalOpen(false)}
      />
    </div>
  );
}
